// WebCrypto core for the PII vault. Pure functions, no Supabase, no React —
// this is the security-critical layer and is intentionally isolated/testable.
//
// Envelope model: one extractable 256-bit AES-GCM DEK encrypts the profile. The
// DEK is wrapped (encrypted) under two independent, non-extractable KEKs:
//   - PIN KEK:     PBKDF2-HMAC-SHA256(pin, salt, 600k)
//   - Passkey KEK: HKDF-SHA256(WebAuthn PRF output)
// Either envelope unwraps the SAME DEK. The DEK must be extractable so it can be
// re-wrapped when adding a passkey or changing the PIN.

import {
  DEK_BYTES,
  IV_BYTES,
  PBKDF2_HASH,
  PBKDF2_ITERATIONS,
  EncryptedBlob,
  WrappedKey,
  ZK_VAULT_VERSION,
} from './types';

// ---- base64 (binary-safe; NOT btoa over UTF-8 text) ----
export function bytesToB64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  // Chunk to avoid call-stack limits on String.fromCharCode(...spread).
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
  }
  return btoa(s);
}

export function b64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

// base64url for credential ids (URL/storage-clean).
export function bytesToB64url(buf: ArrayBuffer | Uint8Array): string {
  return bytesToB64(buf).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64urlToBytes(b64url: string): Uint8Array {
  const pad = b64url.length % 4 === 0 ? '' : '='.repeat(4 - (b64url.length % 4));
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + pad;
  return b64ToBytes(b64);
}

// ---- random ----
export function randomBytes(n: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(n));
}

// ---- DEK ----
// Extractable (required to wrap/re-wrap). 256-bit AES-GCM.
export function generateDEK(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

// ---- PIN KEK: PBKDF2 -> AES-GCM (non-extractable) ----
export async function deriveKeyFromPin(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin.normalize('NFKC')), // normalize unicode PINs
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations: PBKDF2_ITERATIONS, hash: PBKDF2_HASH },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ---- Passkey KEK: HKDF over PRF output -> AES-GCM (non-extractable) ----
// PRF output is already uniform 32 bytes; HKDF adds clean domain separation.
export async function deriveKeyFromPrf(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const hkdfKey = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0) as unknown as BufferSource,
      info: new TextEncoder().encode(`${ZK_VAULT_VERSION}:passkey-kek`),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ---- wrap / unwrap the DEK (AES-GCM over raw DEK bytes) ----
export async function wrapDEK(dek: CryptoKey, kek: CryptoKey): Promise<WrappedKey> {
  const rawDek = await crypto.subtle.exportKey('raw', dek); // 32 bytes (needs extractable DEK)
  const iv = randomBytes(IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as unknown as BufferSource }, kek, rawDek);
  return { v: 1, iv: bytesToB64(iv), ct: bytesToB64(ct) };
}

// Throws on wrong KEK (GCM tag failure) — that is how we detect a bad PIN/passkey.
export async function unwrapDEK(wrapped: WrappedKey, kek: CryptoKey): Promise<CryptoKey> {
  const iv = b64ToBytes(wrapped.iv);
  const rawDek = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    kek,
    b64ToBytes(wrapped.ct) as unknown as BufferSource,
  );
  if (rawDek.byteLength !== DEK_BYTES) throw new Error('Unwrapped key has unexpected length');
  return crypto.subtle.importKey(
    'raw',
    rawDek,
    { name: 'AES-GCM', length: 256 },
    true, // extractable: enables re-wrap (add passkey / change PIN)
    ['encrypt', 'decrypt'],
  );
}

// ---- data encryption ----
export async function encryptData(dek: CryptoKey, plaintext: string): Promise<EncryptedBlob> {
  const iv = randomBytes(IV_BYTES); // FRESH per call — never reuse an (key, IV) pair
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    dek,
    new TextEncoder().encode(plaintext),
  );
  return { v: 1, iv: bytesToB64(iv), ct: bytesToB64(ct) };
}

export async function decryptData(dek: CryptoKey, blob: EncryptedBlob): Promise<string> {
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBytes(blob.iv) as unknown as BufferSource },
    dek,
    b64ToBytes(blob.ct) as unknown as BufferSource,
  );
  return new TextDecoder().decode(pt);
}
